import { toast } from 'sonner';
import { normalizeWorkOrderStatus, WOA_REGISTER_ACTION, WORK_ORDER_STATUS } from '../../../utility/Constant';
import { registerWorkOrderActionApi, workOrderDetailApi } from './workOrders.api';

/**
 * Download a work order's PDF and record it in the history.
 *
 * The PDF is served straight from `pdfUrl`, so the backend cannot see the download happen —
 * we register the action first, then fetch the file. Registering is best-effort: if it fails
 * the download still goes ahead, because losing a history row is better than blocking the user.
 *
 * @param {object} workOrder record carrying `_id`, `pdfUrl` and `workOrderNumber`
 * @param {Function} [registerAction] `mutateAsync` from useRegisterWorkOrderAction; falls back
 *                                    to calling the API directly when not supplied
 */
export const downloadWorkOrderPdf = async (workOrder, registerAction) => {
    if (!workOrder?.pdfUrl) {
        toast.error('PDF is not available yet.');
        return false;
    }

    try {
        if (workOrder?._id) {
            const register = registerAction
                ? registerAction({ type: WOA_REGISTER_ACTION.DOWNLOAD, id: workOrder._id })
                : registerWorkOrderActionApi(WOA_REGISTER_ACTION.DOWNLOAD, workOrder._id);

            await Promise.resolve(register).catch((error) => {
                // History is not worth failing a download over
                console.error('Error registering work order download:', error);
            });
        }

        const fileResponse = await fetch(workOrder.pdfUrl);
        const fileBlob = await fileResponse.blob();

        const url = window.URL.createObjectURL(fileBlob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${workOrder?.workOrderNumber || 'work-order'}.pdf`);

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return true;
    } catch (error) {
        console.error('Error downloading file:', error);
        toast.error('Could not download the PDF.');
        return false;
    }
};

/**
 * Where the appointment's "Request / View Authorization" button should go.
 *
 * An appointment that already has a work order opens it; one that does not opens the work order
 * editor prefilled from the appointment (`/work-orders/new/:appointmentId`), which is also what
 * puts `appointmentId` on the create payload so the backend links the two.
 *
 * The record arrives as `workOrder` or, on older responses, `workOrderAuthorization` — both are the
 * same `work-order-authorization` record, so either is accepted.
 *
 * @param {object} appointment appointment detail record
 * @returns {string} route to navigate to
 */
export const resolveAppointmentWorkOrderRoute = (appointment) => {
    const workOrder = appointment?.workOrder || appointment?.workOrderAuthorization;

    if (workOrder?._id) {
        return `/work-orders/${normalizeWorkOrderStatus(workOrder?.status) || WORK_ORDER_STATUS.OPEN}/${workOrder._id}`;
    }

    return `/work-orders/new/${appointment?._id}`;
};

/**
 * After an update, the record's status is recomputed by the backend and can lag the update
 * response by a moment (spec §4.4). Poll the detail endpoint until it reports the status we expect,
 * so the user is only sent to the detail page once it will render the right status — no landing on
 * "Authorized" and watching it flip to "Reapprove".
 *
 * Gives up after `attempts` tries and returns the last status it saw, so a slow backend delays the
 * redirect a little instead of blocking it.
 *
 * @param {string} id work order id
 * @param {string} expectedStatus normalized status to wait for; falsy skips the wait
 * @param {{ attempts?: number, delayMs?: number }} [options]
 * @returns {Promise<string>} the normalized status last read
 */
export const waitForWorkOrderStatus = async (id, expectedStatus, { attempts = 6, delayMs = 400 } = {}) => {
    let lastStatus = '';
    if (!id) return lastStatus;

    for (let attempt = 0; attempt < attempts; attempt += 1) {
        try {
            // eslint-disable-next-line no-await-in-loop
            const record = await workOrderDetailApi(id);
            lastStatus = normalizeWorkOrderStatus(record?.status) || lastStatus;
        } catch (error) {
            // A failed read is not worth blocking the redirect for.
            break;
        }
        if (!expectedStatus || lastStatus === expectedStatus) break;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, delayMs));
    }

    return lastStatus;
};
